import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import {
  type CalculateMonthlyContributionParams,
  calculateMonthlyContribution,
  calculateMonthlyContributionWithFee,
  calculateMonthlyContributionWithoutFee,
} from "Charts/Utils/CommonChartUtils";
import { calculateTargetAmount as calculateAccumulationTargetAmount } from "Charts/Utils/CommonChartUtils";
import { calculateTargetAmount } from "Charts/Utils/MonthlyWithdrawChartUtils";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { InvestmentCaseContext } from "Components/Analyses/_Shared/InvestmentCaseContext";
import { ConfirmRecalculateTargetModal } from "Components/_Shared/Dialogs/ConfirmRecalculateTargetModal";
import { BlCheckboxInput } from "Forms/Components/BlCheckboxInput";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { BlRadioButtons } from "Forms/Components/BlRadioButtons";
import { DatePicker } from "Forms/Components/DatePicker";
import { StyledLabelAdornment } from "Forms/Components/FormStyles";
import { FormSwitch } from "Forms/Components/FormSwitch";
import { FormTextarea } from "Forms/Components/FormTextarea";
import {
  type OtherGoalFormType as CreateOtherGoalFormType,
  DrawType,
  getOtherGoalFormSchema,
  minDate,
} from "Forms/Schemas/Goals/otherGoalFormSchema";
import {
  getGetAnalysisGoalsQueryKey,
  getGetOtherGoalQueryKey,
  useCreateOtherGoal,
  useUpdateOtherGoal,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  AmountFrequency,
  OtherGoalDataPayoutFrequency,
  type OtherGoalDto,
  OtherGoalDtoPriorityLevel,
  TimeRangeType,
} from "Infrastructure/ApiZfp/Schema";
import {
  DEFAULT_FEE_RATE,
  DEFAULT_INFLATION_RATE,
  INTEREST_RATE_AGGRESIVE,
  INTEREST_RATE_CONSERVATIVE,
} from "Infrastructure/Constants/financialConstants";
import { CurrencyCode } from "Infrastructure/Models/CurrencyCodes";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { addMonths, differenceInMonths, format, isBefore, startOfDay, startOfMonth } from "date-fns";
import { forwardRef, useContext, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { ModelingAccordion } from "./ModelingAccordion";
import { MonthlyWithdrawChart } from "./MonthlyWithdrawChart";
import { OtherGoalModelingHint } from "./OtherGoalModelingHint";

type Props = {
  readonly onClose: () => void;
  readonly onDrawTypeChange: (drawType: DrawType) => void;
  readonly editGoal?: OtherGoalDto;
  readonly goalId?: string;
};

export const OtherGoalMonthlyForm = forwardRef<{}, Props>(({ onDrawTypeChange, onClose, editGoal, goalId }, ref) => {
  const { t } = useResource();
  const queryClient = useQueryClient();
  const { analysisId, versionId } = useParams() as {
    analysisId: string;
    versionId: string;
  };

  const { readOnly: isAnalysisReadOnly } = useContext(AnalysisDetailContext);
  const { isInvestmentCaseLocked } = useContext(InvestmentCaseContext);
  const readOnly = isAnalysisReadOnly || isInvestmentCaseLocked;

  const defaultValues = useMemo(() => {
    if (editGoal) {
      return {
        includeFee: editGoal.modeling.fee?.enabled,
        includeInflation: editGoal.modeling.inflation?.enabled,
        feeRate: Number(editGoal.modeling.fee?.rate) || 0,
        inflationRate: Number(editGoal.modeling.inflation?.rate) || 0,
        name: editGoal.name,
        drawStart: new Date(editGoal.data.targetDate as string),
        drawDuration: Number(editGoal.data.payoutInterval?.value) || 0,
        drawAmount: Number(editGoal.data.payoutAmount?.value) || 0,
        targetAmount: Number(editGoal.modeling.targetAmount?.value) || 0,
        interestRate: Number(editGoal.modeling.appreciationRate) || 0,
        drawdownInterestRate: Number(editGoal.modeling.payoutAppreciationRate) || 0,
        note: editGoal.note || "",
        priority: editGoal.priorityLevel === OtherGoalDtoPriorityLevel.high,
        drawType: DrawType.Monthly,
        referenceStartDate: editGoal.referenceStartDate ? new Date(editGoal.referenceStartDate) : new Date(),
        monthlyContributionWithFee: Number(editGoal.modeling.regularInvestmentAmount?.value) || 0,
      };
    }

    return {
      includeFee: true,
      includeInflation: true,
      feeRate: DEFAULT_FEE_RATE,
      inflationRate: DEFAULT_INFLATION_RATE,
      drawType: DrawType.Monthly,
      name: t(Resources.AnalysisDetail.Goals.GoalType.Other),
      monthlyContribution: 0,
      monthlyContributionWithFee: 0,
      interestRate: INTEREST_RATE_CONSERVATIVE,
      drawdownInterestRate: INTEREST_RATE_CONSERVATIVE,
      referenceStartDate: new Date(),
    };
  }, [editGoal, t]);

  const {
    control,
    setValue,
    watch,
    handleSubmit,
    formState: { errors },
    clearErrors,
  } = useForm<CreateOtherGoalFormType>({
    resolver: yupResolver(getOtherGoalFormSchema(t)),
    mode: "onBlur",
    defaultValues,
  });

  const drawStart = watch("drawStart");
  const drawDuration = watch("drawDuration");
  const drawAmount = watch("drawAmount");
  const targetAmount = watch("targetAmount");
  const feeRate = watch("feeRate");
  const monthlyContribution = watch("monthlyContribution");
  const monthlyContributionWithFee = watch("monthlyContributionWithFee");
  const includeInflation = watch("includeInflation");
  const includeFee = watch("includeFee");
  const inflationRate = watch("inflationRate");
  const interestRate = watch("interestRate");
  const drawdownInterestRate = watch("drawdownInterestRate");
  const referenceStartDate = watch("referenceStartDate");
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [pendingMonthlyContributionWithFee, setPendingMonthlyContributionWithFee] = useState<number | null>(null);
  const [lastAutoMonthlyContributionWithFee, setLastAutoMonthlyContributionWithFee] = useState<number | null>(null);

  const isInvalidDrawStartDate = isBefore(startOfDay(drawStart), startOfDay(minDate));
  const shouldRenderModeling = drawStart && !isInvalidDrawStartDate && monthlyContribution > 0 && targetAmount > 0;
  const shouldRenderChart = drawDuration && drawAmount && drawDuration > 0 && drawAmount > 0;

  const { mutate: createOtherGoal } = useCreateOtherGoal();

  const { mutate: updateOtherGoal } = useUpdateOtherGoal();

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      if (editGoal) {
        handleSubmit(onEdit)();
      } else {
        handleSubmit(onCreate)();
      }
    },
  }));

  useEffect(() => {
    if (!drawStart || isInvalidDrawStartDate) return;
    clearErrors("drawStart");
    onChangeDrawStartDate();
  }, [drawStart, isInvalidDrawStartDate, clearErrors]);

  useEffect(() => {
    if (editGoal || !drawStart || isInvalidDrawStartDate) return;
    const durationInMonths = differenceInMonths(drawStart, referenceStartDate);
    if (durationInMonths && durationInMonths > 120) {
      setValue("interestRate", INTEREST_RATE_AGGRESIVE);
      setValue("drawdownInterestRate", INTEREST_RATE_AGGRESIVE);
    } else {
      setValue("interestRate", INTEREST_RATE_CONSERVATIVE);
      setValue("drawdownInterestRate", INTEREST_RATE_CONSERVATIVE);
    }
  }, [setValue, drawStart, referenceStartDate, isInvalidDrawStartDate, editGoal]);

  useEffect(() => {
    if (!drawStart || isInvalidDrawStartDate) return;
    const durationInMonths = differenceInMonths(drawStart, referenceStartDate);
    if (interestRate !== undefined && targetAmount !== undefined && durationInMonths !== undefined) {
      const monthlyContribution = calculateMonthlyContribution({
        targetAmount,
        durationInMonths,
        interestRate,
        inflationRate: includeInflation ? Number(inflationRate) : 0,
      });
      const wf = calculateMonthlyContributionWithFee(monthlyContribution, feeRate);
      const roundedWf = Math.round(Number(wf));
      setValue("monthlyContribution", monthlyContribution.toString() as any);
      setValue("monthlyContributionWithFee", roundedWf.toString() as any);
      setLastAutoMonthlyContributionWithFee(roundedWf);
    }
  }, [
    feeRate,
    inflationRate,
    interestRate,
    targetAmount,
    includeInflation,
    setValue,
    drawStart,
    isInvalidDrawStartDate,
    referenceStartDate,
  ]);

  const onChangeDrawStartDate = () => {
    onPrimaryParameterChange(drawStart, drawDuration, drawAmount, drawdownInterestRate);
  };

  const onPrimaryParameterChange = (drawStart: Date | null, drawDuration?: number, drawAmount?: number, drawdownInterestRate?: number) => {
    if (drawStart && drawAmount && drawDuration) {
      const durationInMonths = differenceInMonths(drawStart, referenceStartDate);

      if (!editGoal) {
        const calculatedTargetAmount = calculateTargetAmount(drawAmount, drawDuration, drawdownInterestRate || 0, inflationRate);
        setValue("targetAmount", calculatedTargetAmount.toString() as any);
      }

      const monthlyContribution = calculateMonthlyContribution({
        targetAmount,
        durationInMonths,
        interestRate,
        inflationRate: includeInflation ? Number(inflationRate) : 0,
      });
      const wf = calculateMonthlyContributionWithFee(monthlyContribution, feeRate);
      setValue("monthlyContributionWithFee", wf.toString() as any);
    }
  };

  const onSecondaryParameterChange = ({
    inflationRate,
    interestRate,
    targetAmount,
    durationInMonths,
    newFeeRate,
  }: CalculateMonthlyContributionParams) => {
    if (interestRate && targetAmount && durationInMonths) {
      const monthlyContribution = calculateMonthlyContribution({
        targetAmount: targetAmount,
        durationInMonths,
        interestRate,
        inflationRate: includeInflation ? Number(inflationRate) : 0,
      });
      const wf = calculateMonthlyContributionWithFee(Number(monthlyContribution), newFeeRate ?? feeRate);
      const roundedWf = Math.round(Number(wf));
      setValue("monthlyContribution", monthlyContribution.toString() as any);
      setValue("monthlyContributionWithFee", roundedWf.toString() as any);
      setLastAutoMonthlyContributionWithFee(roundedWf);
    }
  };

  const recalculateMonthlyContributionFromTarget = () => {
    const durationInMonths = drawStart ? differenceInMonths(drawStart, referenceStartDate) : 0;
    const monthlyContribution = calculateMonthlyContribution({
      targetAmount,
      durationInMonths,
      interestRate,
      inflationRate: includeInflation ? Number(inflationRate) : 0,
    });
    const wf = calculateMonthlyContributionWithFee(Number(monthlyContribution), Number(includeFee ? feeRate : 0));
    const roundedWf = Math.round(Number(wf));
    setValue("monthlyContribution", monthlyContribution.toString() as any);
    setValue("monthlyContributionWithFee", roundedWf.toString() as any);
    setLastAutoMonthlyContributionWithFee(roundedWf);
  };

  const onMonthlyContributionChange = (newContributionWithFee: string) => {
    setValue("monthlyContribution", calculateMonthlyContributionWithoutFee(Number(newContributionWithFee), feeRate));
  };

  const handleConfirmResult = (shouldRecalculateTarget: boolean) => {
    if (shouldRecalculateTarget && pendingMonthlyContributionWithFee != null) {
      const durationInMonths = drawStart ? differenceInMonths(drawStart, referenceStartDate) : 0;
      const monthsToYears = durationInMonths / 12;
      const contributionWithoutFee = calculateMonthlyContributionWithoutFee(
        Number(pendingMonthlyContributionWithFee),
        Number(includeFee ? feeRate : 0),
      );

      const newTarget = calculateAccumulationTargetAmount(
        Number(contributionWithoutFee),
        monthsToYears,
        Number(interestRate) || 0,
        includeInflation ? Number(inflationRate) || 0 : 0,
      );

      setValue("targetAmount", Math.round(newTarget) as any);
      setValue("monthlyContribution", contributionWithoutFee.toString() as any);
      const roundedWithFee = Math.round(Number(pendingMonthlyContributionWithFee));
      setValue("monthlyContributionWithFee", roundedWithFee as any);
      setLastAutoMonthlyContributionWithFee(roundedWithFee);
    } else {
      recalculateMonthlyContributionFromTarget();
    }

    setIsConfirmOpen(false);
    setPendingMonthlyContributionWithFee(null);
  };

  const onTargetAmountChange = (newTargetAmount: string) => {
    const monthlyContribution = calculateMonthlyContribution({
      targetAmount: Number(newTargetAmount),
      durationInMonths: drawDuration || 0,
      interestRate,
      inflationRate: includeInflation ? Number(inflationRate) : 0,
    });

    setValue("monthlyContributionWithFee", calculateMonthlyContributionWithFee(monthlyContribution, feeRate).toString() as any);
  };

  const onCreate = (data: CreateOtherGoalFormType) => {
    createOtherGoal(
      {
        analysisId,
        versionId,
        data: {
          name: data.name,
          note: data.note || "",
          priorityLevel: data.priority ? OtherGoalDtoPriorityLevel.high : OtherGoalDtoPriorityLevel.low,
          data: {
            targetDate: format(data.drawStart, "yyyy-MM-dd"),
            payoutFrequency: OtherGoalDataPayoutFrequency.regular,
            payoutInterval: {
              value: drawDuration || 0,
              type: TimeRangeType.month,
            },
            payoutAmount: {
              value: data.drawAmount || 0,
              currencyCode: "CZK",
              frequency: AmountFrequency.monthly,
            },
          },
          modeling: {
            targetAmount: {
              value: data.targetAmount,
              currencyCode: "CZK",
              frequency: AmountFrequency.one_time,
            },
            inflation: {
              enabled: !!includeInflation,
              rate: data.inflationRate,
            },
            fee: {
              enabled: !!includeFee,
              rate: data.feeRate,
            },
            regularInvestmentAmount: {
              value: includeFee ? data.monthlyContributionWithFee : data.monthlyContribution,
              currencyCode: "CZK",
              frequency: AmountFrequency.monthly,
            },
            appreciationRate: data.interestRate,
            payoutAppreciationRate: data.drawdownInterestRate || 0,
          },
        },
      },
      {
        onSuccess: () => {
          toast.success(t(Resources.AnalysisDetail.Toast.GoalCreated));
          onClose();
          queryClient.invalidateQueries({ queryKey: getGetAnalysisGoalsQueryKey(analysisId, versionId) });
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Toast.GoalError));
        },
      },
    );
  };

  const onEdit = (data: CreateOtherGoalFormType) => {
    updateOtherGoal(
      {
        analysisId,
        versionId,
        goalId: editGoal?.goalId ?? "",
        data: {
          name: data.name,
          note: data.note || "",
          priorityLevel: data.priority ? OtherGoalDtoPriorityLevel.high : OtherGoalDtoPriorityLevel.low,
          data: {
            targetDate: format(data.drawStart, "yyyy-MM-dd"),
            payoutFrequency: OtherGoalDataPayoutFrequency.regular,
            payoutInterval: {
              value: drawDuration || 0,
              type: TimeRangeType.month,
            },
            payoutAmount: {
              value: data.drawAmount || 0,
              currencyCode: "CZK",
              frequency: AmountFrequency.monthly,
            },
          },
          modeling: {
            regularInvestmentAmount: {
              value: data.includeFee ? data.monthlyContributionWithFee : data.monthlyContribution,
              currencyCode: "CZK",
              frequency: AmountFrequency.monthly,
            },
            targetAmount: {
              value: data.targetAmount,
              currencyCode: "CZK",
              frequency: AmountFrequency.one_time,
            },
            fee: {
              enabled: !!data.includeFee,
              rate: data.feeRate,
            },
            inflation: {
              enabled: !!data.includeInflation,
              rate: data.inflationRate,
            },
            appreciationRate: data.interestRate,
            payoutAppreciationRate: data.drawdownInterestRate || 0,
          },
        },
      },
      {
        onSuccess: () => {
          toast.success(t(Resources.AnalysisDetail.Toast.GoalUpdated));
          onClose();
          queryClient.invalidateQueries({ queryKey: getGetAnalysisGoalsQueryKey(analysisId, versionId) });
          queryClient.invalidateQueries({
            queryKey: getGetOtherGoalQueryKey(analysisId, versionId, goalId ?? editGoal?.goalId ?? undefined),
          });
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Toast.GoalError));
        },
      },
    );
  };

  return (
    <form>
      <Stack spacing={Spacing.XXL}>
        <ConfirmRecalculateTargetModal isOpen={isConfirmOpen} handleResult={handleConfirmResult} />
        <Box>
          <Typography fontWeight={600} variant="body1" mb={Spacing.XL}>
            {t(Resources.AnalysisDetail.Goals.GoalType.Other)}
          </Typography>
          <Grid container spacing={Spacing.XL}>
            <Grid size={12}>
              <BlCheckboxInput
                control={control}
                disabled={readOnly}
                name="priority"
                text={t(Resources.AnalysisDetail.Goals.GoalPriorityLabel)}
                errors={errors}
              />
            </Grid>
            <Grid size={12}>
              <BlFormInput
                control={control}
                disabled={readOnly}
                name="name"
                label={t(Resources.AnalysisDetail.Goals.GoalName)}
                errors={errors}
                labelEndAdornment={<StyledLabelAdornment />}
              />
            </Grid>
            <Grid size={12}>
              <BlRadioButtons
                control={control}
                name="drawType"
                disabled={!!editGoal || readOnly}
                label={t(Resources.AnalysisDetail.Goals.DrawType)}
                onChange={(drawType) => onDrawTypeChange(drawType === DrawType.LumpSum.toString() ? DrawType.LumpSum : DrawType.Monthly)}
                codeList={[
                  {
                    code: DrawType.LumpSum,
                    name: t(Resources.AnalysisDetail.Goals.LumpSum),
                  },
                  {
                    code: DrawType.Monthly,
                    name: t(Resources.AnalysisDetail.Goals.Monthly),
                  },
                ]}
                errors={errors}
              />
            </Grid>
            <Grid size={4}>
              <DatePicker
                control={control}
                disabled={readOnly}
                name="drawStart"
                label={t(Resources.AnalysisDetail.Goals.DrawStart)}
                labelEndAdornment={<StyledLabelAdornment />}
                minDate={startOfMonth(addMonths(new Date(), 1))}
                errors={errors}
              />
            </Grid>
            <Grid size={4}>
              <BlFormInput
                control={control}
                disabled={readOnly}
                name="drawDuration"
                label={t(Resources.AnalysisDetail.Goals.DrawDuration)}
                labelEndAdornment={<StyledLabelAdornment />}
                errors={errors}
                onChange={(newDrawDuration) => {
                  onPrimaryParameterChange(drawStart, Number(newDrawDuration), drawAmount, drawdownInterestRate);
                }}
                unmask="typed"
                mask={[
                  {
                    mask: Number,
                  },
                ]}
                inputEndAdornment={
                  <Typography component="span" variant="body2" color="secondary">
                    {t(Resources.AnalysisDetail.Goals.Months)}
                  </Typography>
                }
              />
            </Grid>
            <Grid size={4}>
              <BlFormInput
                control={control}
                disabled={readOnly}
                name="drawAmount"
                label={t(Resources.AnalysisDetail.Goals.DrawAmount)}
                labelEndAdornment={<StyledLabelAdornment />}
                errors={errors}
                onChange={(newDrawAmount) => {
                  onPrimaryParameterChange(drawStart, drawDuration, Number(newDrawAmount), drawdownInterestRate);
                }}
                unmask="typed"
                mask={[
                  {
                    mask: Number,
                    thousandsSeparator: " ",
                  },
                ]}
                inputEndAdornment={
                  <Box sx={{ width: 200 }}>
                    <Typography component="span" variant="body2" color="secondary">
                      {t(Resources.Common.CZKSuffix)}/{t(Resources.Common.Monthly)}
                    </Typography>
                  </Box>
                }
              />
            </Grid>
          </Grid>
        </Box>
        {targetAmount > 0 && drawStart && !isInvalidDrawStartDate && (
          <Box>
            <ModelingAccordion>
              <Grid container spacing={Spacing.XL} mt={Spacing.XL}>
                <Grid size={12}>
                  <OtherGoalModelingHint
                    referenceStartDate={referenceStartDate}
                    startDate={drawStart}
                    interestRate={interestRate}
                    targetAmount={targetAmount}
                    monthlyContribution={monthlyContributionWithFee}
                  />
                </Grid>
                <Grid size={6}>
                  <BlFormInput
                    control={control}
                    disabled={readOnly}
                    name="targetAmount"
                    label={t(Resources.AnalysisDetail.Goals.TargetAmount)}
                    labelEndAdornment={<StyledLabelAdornment />}
                    errors={errors}
                    unmask="typed"
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 2,
                        radix: ",",
                        mapToRadix: ["."],
                      },
                    ]}
                    inputEndAdornment={
                      <Typography component="span" variant="body2" color="secondary">
                        {t(Resources.Common.CZKSuffix)}
                      </Typography>
                    }
                    onChange={onTargetAmountChange}
                  />
                </Grid>
                <Grid size={6}>
                  <BlFormInput
                    control={control}
                    disabled={readOnly}
                    name="monthlyContributionWithFee"
                    label={t(Resources.AnalysisDetail.Goals.MonthlyContribution)}
                    labelEndAdornment={<StyledLabelAdornment />}
                    errors={errors}
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 0,
                        min: 0,
                      },
                    ]}
                    unmask="typed"
                    inputEndAdornment={
                      <Typography component="span" variant="body2" color="secondary">
                        {t(Resources.Common.CZKSuffix)}
                      </Typography>
                    }
                    onChange={(newMonthlyContributionWithFee) => {
                      onMonthlyContributionChange(newMonthlyContributionWithFee);
                    }}
                    onBlur={() => {
                      const currentValue = Math.round(Number(monthlyContributionWithFee));
                      if (lastAutoMonthlyContributionWithFee !== null && currentValue === Number(lastAutoMonthlyContributionWithFee)) {
                        return;
                      }
                      setPendingMonthlyContributionWithFee(currentValue);
                      setIsConfirmOpen(true);
                    }}
                  />
                </Grid>
                <Grid size={6}>
                  <BlFormInput
                    control={control}
                    disabled={readOnly}
                    name="interestRate"
                    label={t(Resources.AnalysisDetail.Goals.InterestRate)}
                    labelEndAdornment={<StyledLabelAdornment />}
                    errors={errors}
                    unmask="typed"
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        radix: ",",
                        mapToRadix: ["."],
                        scale: 1,
                        min: includeInflation ? 0 : 0.1,
                      },
                    ]}
                    onChange={(newInterestRate) => {
                      if (Number(newInterestRate) === Number(inflationRate) || (!includeInflation && Number(newInterestRate) <= 0)) {
                        return;
                      }
                      onSecondaryParameterChange({
                        durationInMonths: differenceInMonths(drawStart, referenceStartDate),
                        targetAmount,
                        inflationRate,
                        interestRate: Number(newInterestRate),
                      });
                    }}
                    inputEndAdornment={
                      <Typography component="span" variant="body2" color="secondary">
                        %
                      </Typography>
                    }
                  />
                </Grid>
                <Grid size={6}>
                  <BlFormInput
                    control={control}
                    disabled={readOnly}
                    name="drawdownInterestRate"
                    label={t(Resources.AnalysisDetail.Goals.DrawdownInterestRate)}
                    labelEndAdornment={<StyledLabelAdornment />}
                    errors={errors}
                    inputEndAdornment={
                      <Typography component="span" variant="body2" color="secondary">
                        %
                      </Typography>
                    }
                    unmask="typed"
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        radix: ",",
                        mapToRadix: ["."],
                        scale: 1,
                        min: includeInflation ? 0 : 0.1,
                      },
                    ]}
                    onChange={(newDrawdownInterestRate) => {
                      if (
                        Number(newDrawdownInterestRate) === Number(inflationRate) ||
                        (!includeInflation && Number(newDrawdownInterestRate) <= 0)
                      ) {
                        setValue("drawdownInterestRate", drawdownInterestRate);
                        return;
                      }
                      onPrimaryParameterChange(drawStart, drawDuration, drawAmount, Number(newDrawdownInterestRate));
                    }}
                  />
                </Grid>
                <Grid size={9} display="flex" alignItems="center">
                  <FormSwitch
                    control={control}
                    disabled={readOnly}
                    name="includeInflation"
                    label={t(Resources.AnalysisDetail.Goals.IncludeInflation)}
                    onChange={(checked) => {
                      const newInflationRate = checked ? DEFAULT_INFLATION_RATE : 0;

                      if (newInflationRate === Number(interestRate)) {
                        setValue("inflationRate", (newInflationRate + 0.1).toString() as any);
                      } else {
                        setValue("inflationRate", newInflationRate.toString() as any);
                      }

                      onSecondaryParameterChange({
                        durationInMonths: drawStart ? differenceInMonths(drawStart, referenceStartDate) : 0,
                        targetAmount,
                        inflationRate: newInflationRate,
                        interestRate,
                      });

                      setValue("inflationRate", newInflationRate);
                    }}
                    errors={errors}
                  />
                </Grid>
                <Grid size={3}>
                  <BlFormInput
                    disabled={!includeInflation || readOnly}
                    control={control}
                    name="inflationRate"
                    unmask="typed"
                    mask={[
                      {
                        mask: Number,
                        radix: ",",
                        mapToRadix: ["."],
                        min: 0,
                        max: 20,
                        scale: 1,
                      },
                    ]}
                    errors={errors}
                    onChange={(newInflationRate) => {
                      if (
                        (includeInflation && Number(drawdownInterestRate) === Number(newInflationRate)) ||
                        (!includeInflation && Number(newInflationRate) <= 0)
                      ) {
                        setValue("inflationRate", inflationRate);
                        return;
                      }

                      onSecondaryParameterChange({
                        durationInMonths: drawStart ? differenceInMonths(drawStart, new Date()) : 0,
                        targetAmount,
                        inflationRate: Number(newInflationRate),
                        interestRate,
                      });
                    }}
                    inputEndAdornment={
                      <Typography component="span" variant="body2" color="secondary">
                        %
                      </Typography>
                    }
                  />
                </Grid>
                <Grid size={9} display="flex" alignItems="center">
                  <FormSwitch
                    control={control}
                    disabled={readOnly}
                    name="includeFee"
                    label={t(Resources.AnalysisDetail.Goals.IncludeEntryFee)}
                    onChange={(checked) => {
                      const newFeeRate = checked ? DEFAULT_FEE_RATE : 0;

                      setValue("feeRate", newFeeRate);
                      onSecondaryParameterChange({
                        durationInMonths: drawStart ? differenceInMonths(drawStart, referenceStartDate) : 0,
                        targetAmount,
                        inflationRate,
                        interestRate,
                        newFeeRate,
                      });
                    }}
                    errors={errors}
                  />
                </Grid>
                <Grid size={3}>
                  <BlFormInput
                    disabled={!includeFee || readOnly}
                    control={control}
                    name="feeRate"
                    unmask="typed"
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        radix: ",",
                        mapToRadix: ["."],
                        scale: 1,
                        min: 0,
                        max: 20,
                      },
                    ]}
                    errors={errors}
                    inputEndAdornment={
                      <Typography component="span" variant="body2" color="secondary">
                        %
                      </Typography>
                    }
                  />
                </Grid>
              </Grid>
            </ModelingAccordion>
          </Box>
        )}
        <Box>
          <Grid container spacing={Spacing.XL}>
            {shouldRenderModeling && (
              <Grid size={12}>
                <Box padding={Spacing.S} display="flex" justifyContent="space-around" alignItems="center">
                  <Stack spacing={Spacing.XS} display="flex" justifyContent="center" alignItems="center">
                    <Typography component="span" variant="caption" fontWeight={500}>
                      {t(Resources.Common.Investment)}
                    </Typography>
                    <Typography component="span" variant="caption">
                      {formatCurrency(Math.round(Number(monthlyContributionWithFee)), CurrencyCode.CZK)}/{t(Resources.Common.Month)}
                    </Typography>
                  </Stack>
                  <Stack spacing={Spacing.XS} display="flex" justifyContent="center" alignItems="center">
                    <Typography component="span" variant="caption" fontWeight={500}>
                      {t(Resources.AnalysisDetail.Goals.DrawProcess)}
                    </Typography>
                    <Typography component="span" variant="caption">
                      {formatCurrency(drawAmount, CurrencyCode.CZK)}/{t(Resources.Common.Month)}
                    </Typography>
                  </Stack>
                </Box>
                {shouldRenderChart && (
                  <MonthlyWithdrawChart
                    referenceStartDate={referenceStartDate}
                    targetDate={drawStart}
                    monthlyContributionAmount={Number(monthlyContribution) || 0}
                    inflationRate={includeInflation ? Number(inflationRate) : 0}
                    accumulationInterestRate={interestRate}
                    drawdownPhaseDuration={Number(drawDuration) || 0}
                    drawdownInterestRate={drawdownInterestRate || 0}
                    drawAmount={drawAmount}
                    targetAmount={targetAmount}
                  />
                )}
              </Grid>
            )}
            <Grid size={12}>
              <FormTextarea
                control={control}
                disabled={readOnly}
                name="note"
                label={t(Resources.AnalysisDetail.Goals.GoalNote)}
                errors={errors}
              />
            </Grid>
          </Grid>
        </Box>
      </Stack>
    </form>
  );
});
